import { useMemo } from 'react';

import { DOTS } from './constants';

type Options = {
  totalPages: number;
  currentPage: number;
  siblingCount: number;
  boundaryCount: number;
};

export type PaginationItem = number | typeof DOTS;

const range = (start: number, end: number): number[] => {
  if (end < start) {
    return [];
  }
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

export const usePaginationRange = ({
  totalPages,
  currentPage,
  siblingCount,
  boundaryCount,
}: Options): PaginationItem[] => {
  return useMemo(() => {
    if (totalPages <= 0) {
      return [];
    }

    const page = currentPage + 1;
    const count = totalPages;

    const startPages = range(1, Math.min(boundaryCount, count));
    const endPages = range(
      Math.max(count - boundaryCount + 1, boundaryCount + 1),
      count
    );

    const siblingsStart = Math.max(
      Math.min(
        page - siblingCount,
        count - boundaryCount - siblingCount * 2 - 1
      ),
      boundaryCount + 2
    );

    const firstEndPage = endPages[0];
    const siblingsEnd = Math.min(
      Math.max(page + siblingCount, boundaryCount + siblingCount * 2 + 2),
      firstEndPage !== undefined ? firstEndPage - 2 : count - 1
    );

    const items: PaginationItem[] = [
      ...startPages,
      ...(siblingsStart > boundaryCount + 2
        ? [DOTS]
        : boundaryCount + 1 < count - boundaryCount
          ? [boundaryCount + 1]
          : []),
      ...range(siblingsStart, siblingsEnd),
      ...(siblingsEnd < count - boundaryCount - 1
        ? [DOTS]
        : count - boundaryCount > boundaryCount
          ? [count - boundaryCount]
          : []),
      ...endPages,
    ];

    return items.map((item) => (typeof item === 'number' ? item - 1 : item));
  }, [totalPages, currentPage, siblingCount, boundaryCount]);
};
